import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Observable, Subject, forkJoin, merge, of } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, map, takeUntil } from 'rxjs/operators';
import { TiposDocumentalesService } from '../../../../../shared/services/tipos-documentales.service';
import { PilaresPesvService } from '../../../../../shared/services/pilares-pesv.service';
import { EstadosDocumentoService } from '../../../../../shared/services/estados-documento.service';
import { NombreDocumentosService } from '../../../../../shared/services/nombre-documentos.service';
import { AdministracionService } from '../../../../../shared/services/administracion.services';
import { ImplementacionService, DocumentoFormData, RutaVersion } from '../../../../../shared/services/implementacion.service';
import { GoogleStoragePreviewService } from '../../../../../shared/services/google-storage-preview.service';
const Swal = require("sweetalert2");

@Component({
  selector: 'app-registrar-retencion',
  templateUrl: './registrar-retencion.component.html',
  styleUrls: ['./registrar-retencion.component.scss']
})
export class RegistrarRetencionComponent implements OnInit, OnDestroy {

  @ViewChild('archivoInput') archivoInput?: ElementRef<HTMLInputElement>;

  activePanel: string[] = ['static-1'];
  validate: boolean = false;
  errores: any = {};

  // Estados de carga / guardado (spinners y botones deshabilitados en la plantilla)
  cargandoCatalogos: boolean = true;
  cargandoDocumento: boolean = false;
  cargandoVersiones: boolean = false;
  cargandoHistorial: boolean = false;
  guardandoDocumento: boolean = false;
  guardandoVersion: boolean = false;
  guardandoEdicion: boolean = false;
  visualizandoId: number | null = null;
  descargandoId: number | null = null;
  vigenteId: number | null = null;

  // Catálogos
  nombresDocumentos: any[] = [];
  tiposDocumentales: any[] = [];
  pilaresPesv: any[] = [];
  estadosDocumento: any[] = [];

  formData: DocumentoFormData;

  // Formulario "nueva versión"
  versionData: any = this.crearVersionVacia();
  archivoNuevo: File | null = null;
  rutaArchivoPreview: string = '';

  // Modal "editar versión" (independiente del formulario de nueva versión)
  versionEdit: any = this.crearVersionVacia();
  archivoEdicion: File | null = null;
  rutaEdicionPreview: string = '';

  historialCambios: any[] = [];
  historialPageSize: number = 10;
  historialPage: number = 1;
  versiones: any[] = [];
  documento_id: number | null = null;
  empresaNit: string | null = null;
  hoy: string = '';

  /** Índice id → nombre del documento: evita recorrer el catálogo en cada validación o ruta. */
  private nombresPorId = new Map<number, string>();

  /** Se emite al destruir el componente: cancela todas las peticiones en curso. */
  private destroy$ = new Subject<void>();
  /** Se emite al cambiar el ID: cancela las cargas del documento anterior. */
  private cambioDocumento$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private tiposService: TiposDocumentalesService,
    private pilaresService: PilaresPesvService,
    private estadosService: EstadosDocumentoService,
    private nombreDocumentosService: NombreDocumentosService,
    private administracionService: AdministracionService,
    private implementacionService: ImplementacionService,
    private vistaPrevia: GoogleStoragePreviewService
  ) {
    this.formData = this.implementacionService.crearFormularioVacio();
  }

  ngOnInit(): void {
    this.hoy = this.implementacionService.fechaLocalISO();

    // Catálogos + NIT en paralelo (una sola suscripción, un solo render al terminar)
    this.cargarCatalogosYNit();

    // Si llega ?id=... se edita ese documento
    this.route.queryParams.pipe(
      map(params => Number(params['id'])),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(id => {
      if (Number.isInteger(id) && id > 0) {
        this.cargarDocumentoParaEditar(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.cambioDocumento$.next();
    this.cambioDocumento$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackById(_: number, item: any): any {
    return item?.id;
  }

  togglePanel(panelId: string): void {
    this.activePanel = this.activePanel.includes(panelId) ? [] : [panelId];
  }

  // ───────────────────────── Carga de datos ─────────────────────────

  /** Convierte una petición en "tolerante a fallos": si falla, anota la etiqueta y devuelve un valor por defecto. */
  private tolerante<T>(obs: Observable<T>, etiqueta: string, porDefecto: T, fallidos: string[]): Observable<T> {
    return obs.pipe(
      catchError((err) => {
        console.error(`Error cargando ${etiqueta}:`, err);
        fallidos.push(etiqueta);
        return of(porDefecto);
      })
    );
  }

  private cargarCatalogosYNit(): void {
    const fallidos: string[] = [];

    forkJoin({
      nombres: this.tolerante<any[]>(this.nombreDocumentosService.getAllNombreDocumentos(), 'nombres de documentos', [], fallidos),
      tipos: this.tolerante<any[]>(this.tiposService.getAllTipos(), 'tipos documentales', [], fallidos),
      pilares: this.tolerante<any[]>(this.pilaresService.getAllPilares(), 'pilares PESV', [], fallidos),
      estados: this.tolerante<any[]>(this.estadosService.getAllEstados(), 'estados', [], fallidos),
      nit: this.tolerante<string | null>(
        this.administracionService.obtenerTodas().pipe(
          map((res: any) => {
            const lista = Array.isArray(res)
              ? res
              : Array.isArray(res?.administraciones)
                ? res.administraciones
                : [];
            return lista.length > 0 ? (lista[0].nit?.toString() || null) : null;
          })
        ),
        'NIT de la empresa',
        null,
        fallidos
      )
    }).pipe(
      finalize(() => { this.cargandoCatalogos = false; }),
      takeUntil(this.destroy$)
    ).subscribe(({ nombres, tipos, pilares, estados, nit }) => {
      // `valor` es el ID que valida el backend (id_defecto); si no existe se usa `id`
      this.nombresDocumentos = (nombres || []).map((n: any) => ({ ...n, valor: n.id_defecto ?? n.id }));
      this.nombresPorId = new Map<number, string>(
        this.nombresDocumentos.map((n: any): [number, string] =>
          [Number(n.id ?? n.valor), n.nombre_documento || n.nombre || '']
        )
      );
      this.tiposDocumentales = tipos || [];
      this.pilaresPesv = pilares || [];
      this.estadosDocumento = estados || [];
      this.empresaNit = nit;

      this.actualizarRutaPreview();

      if (fallidos.length > 0) {
        this.alerta(
          'warning',
          'Carga incompleta',
          `No se pudo cargar: ${fallidos.join(', ')}. Recargue la página si el problema persiste.`
        );
      }
    });
  }

  private cargarDocumentoParaEditar(id: number): void {
    this.cambioDocumento$.next();
    this.documento_id = id;
    this.versiones = [];
    this.historialCambios = [];
    this.cargandoDocumento = true;

    // Versiones e historial solo dependen del ID: se piden en paralelo con el documento
    this.cargarVersiones();
    this.cargarHistorial();

    this.implementacionService.getDocumento(id).pipe(
      finalize(() => { this.cargandoDocumento = false; }),
      takeUntil(merge(this.destroy$, this.cambioDocumento$))
    ).subscribe({
      next: (documento: any) => {
        this.formData = this.implementacionService.formularioDesdeDocumento(documento);
        this.actualizarRutaPreview();
      },
      error: (err: any) => {
        console.error('Error al cargar documento:', err);
        this.alerta('error', 'Error', 'No se pudo cargar el documento');
      }
    });
  }

  cargarHistorial(): void {
    if (!this.documento_id) { return; }
    this.cargandoHistorial = true;
    this.implementacionService.getHistorial(this.documento_id).pipe(
      finalize(() => { this.cargandoHistorial = false; }),
      takeUntil(merge(this.destroy$, this.cambioDocumento$))
    ).subscribe({
      next: (res: any) => {
        this.historialCambios = Array.isArray(res) ? res : [];
        this.historialPage = 1;
      },
      error: (err: any) => console.error('Error al cargar historial:', err)
    });
  }

  cargarVersiones(): void {
    if (!this.documento_id) { return; }
    this.cargandoVersiones = true;
    this.implementacionService.getVersiones(this.documento_id).pipe(
      finalize(() => { this.cargandoVersiones = false; }),
      takeUntil(merge(this.destroy$, this.cambioDocumento$))
    ).subscribe({
      next: (res: any) => { this.versiones = Array.isArray(res) ? res : []; },
      error: (err: any) => console.error('Error al cargar versiones:', err)
    });
  }

  // ───────────────────────── Documento maestro ─────────────────────────

  guardarDocumento(): void {
    if (this.guardandoDocumento) { return; }

    this.validate = true;
    this.errores = this.implementacionService.validarDocumento(this.formData);
    if (Object.keys(this.errores).length > 0) {
      return;
    }

    if (this.documento_id) {
      this.guardandoDocumento = true;
      this.implementacionService.guardarDocumento(this.documento_id, this.formData).pipe(
        finalize(() => { this.guardandoDocumento = false; }),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          this.alerta('success', 'Éxito', 'Documento actualizado exitosamente');
          this.validate = false;
          this.actualizarRutaPreview();
        },
        error: (err: any) => {
          console.error('Error al actualizar:', err);
          this.alerta('error', 'Error', this.mensajeError(err, 'Error al actualizar el documento'));
        }
      });
      return;
    }

    this.guardandoDocumento = true;
    this.implementacionService.guardarDocumento(null, this.formData).pipe(
      finalize(() => { this.guardandoDocumento = false; }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (res: any) => {
        this.documento_id = res.id;
        this.alerta('success', 'Éxito', 'Documento maestro creado correctamente.');
        this.validate = false;
        this.actualizarRutaPreview();
      },
      error: (err: any) => {
        console.error('Error al guardar:', err);
        const advertencia = err?.status === 400;
        this.alerta(advertencia ? 'warning' : 'error', advertencia ? 'Importante' : 'Error',
          this.mensajeError(err, 'Error al guardar el documento'));
      }
    });
  }

  // ───────────────────────── Versiones ─────────────────────────

  registrarVersion(): void {
    if (this.guardandoVersion) { return; }

    this.validate = true;
    this.errores = {};

    if (!this.documento_id) {
      this.alerta('warning', 'Importante', 'Debe guardar el documento primero');
      return;
    }
    if (!this.empresaNit) {
      this.alerta('warning', 'Importante', 'No se ha cargado el NIT de la empresa. Por favor recargue la página');
      return;
    }
    if (!this.formData.codigo || !this.nombreDocumento()) {
      this.alerta('warning', 'Importante', 'El documento debe tener código y nombre guardados para registrar versiones');
      return;
    }

    const numero = String(this.versionData.numero_version || '').trim();
    if (!numero) {
      this.errores.numero_version = 'El número de versión es requerido';
      return;
    }
    if (this.fechaEsFutura(this.versionData.fecha_aprobacion)) {
      this.alerta('warning', 'Fecha inválida', 'La fecha de aprobación no puede ser una fecha futura.');
      return;
    }

    // Se valida ANTES de subir para no sobrescribir en GCS el archivo de una versión existente
    const clave = this.implementacionService.normalizarVersion(numero);
    if (this.versiones.some(v => this.implementacionService.normalizarVersion(v.numero_version) === clave)) {
      this.alerta('warning', 'Versión duplicada', `Ya existe la versión ${numero} de este documento. Use otro número o edite la existente.`);
      return;
    }
    if (!this.archivoNuevo) {
      this.alerta('warning', 'Importante', 'Debe seleccionar un archivo para registrar la versión');
      return;
    }

    const ruta = this.parametrosRuta(numero);
    if (!ruta) {
      this.alerta('warning', 'Importante', 'Faltan datos para construir la ruta del archivo (NIT, código, tipo documental o versión)');
      return;
    }

    this.guardandoVersion = true;
    this.implementacionService.subir(ruta, this.archivoNuevo).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (urlArchivo) => this.crearVersion(numero, urlArchivo),
      error: (err: any) => {
        this.guardandoVersion = false;
        this.errorSubida(err);
      }
    });
  }

  private crearVersion(numero: string, urlArchivo: string): void {
    const payload = {
      documento_id: this.documento_id,
      numero_version: numero,
      fecha_aprobacion: this.versionData.fecha_aprobacion,
      aprobado_por: this.versionData.aprobado_por,
      es_vigente: this.versionData.es_vigente,
      archivo: urlArchivo
    };

    this.implementacionService.guardarVersion(payload).pipe(
      finalize(() => { this.guardandoVersion = false; }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.alerta('success', 'Éxito', 'Versión registrada exitosamente');
        this.cargarHistorial();
        this.versionData = this.crearVersionVacia();
        this.archivoNuevo = null;
        this.rutaArchivoPreview = '';
        if (this.archivoInput) { this.archivoInput.nativeElement.value = ''; }
        this.validate = false;
        this.cargarVersiones();
      },
      error: (err: any) => {
        console.error('Error al registrar versión:', err);
        const duplicada = err?.status === 400 && String(err?.error?.error || '').includes('Ya existe');
        this.alerta(
          duplicada ? 'warning' : 'error',
          duplicada ? 'Versión duplicada' : 'Error',
          this.mensajeError(err, 'Error al registrar la versión')
        );
      }
    });
  }

  marcarComoVigente(version: any): void {
    Swal.fire({
      title: '¿Estás seguro?',
      text: `¿Deseas marcar la versión ${version.numero_version} como vigente?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Aceptar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33'
    }).then((result: any) => {
      if (!result.isConfirmed) { return; }
      this.vigenteId = version.id;
      this.implementacionService.marcarVersionVigente(version.id).pipe(
        finalize(() => { this.vigenteId = null; }),
        takeUntil(this.destroy$)
      ).subscribe({
        next: () => {
          this.alerta('success', 'Éxito', 'Versión marcada como vigente correctamente');
          this.cargarVersiones();
          this.cargarHistorial();
        },
        error: (err: any) => {
          console.error('Error al marcar como vigente:', err);
          this.alerta('error', 'Error', 'No se pudo marcar la versión como vigente');
        }
      });
    });
  }

  editarVersion(version: any, modalContent: any): void {
    this.versionEdit = {
      versionId: version.id,
      numero_version: version.numero_version,
      fecha_aprobacion: version.fecha_aprobacion ? String(version.fecha_aprobacion).substring(0, 10) : '',
      aprobado_por: version.aprobado_por || '',
      es_vigente: !!version.es_vigente
    };
    this.archivoEdicion = null;
    this.rutaEdicionPreview = '';

    this.modalService.open(modalContent, {
      centered: true,
      size: 'lg',
      backdrop: 'static',
      keyboard: false
    });
  }

  guardarVersionEditada(modal: any): void {
    if (this.guardandoEdicion) { return; }

    if (!this.versionEdit.numero_version) {
      this.alerta('warning', 'Validación', 'El número de versión es requerido');
      return;
    }
    if (this.fechaEsFutura(this.versionEdit.fecha_aprobacion)) {
      this.alerta('warning', 'Fecha inválida', 'La fecha de aprobación no puede ser una fecha futura.');
      return;
    }

    const cambios: any = {
      numero_version: this.versionEdit.numero_version,
      fecha_aprobacion: this.versionEdit.fecha_aprobacion,
      aprobado_por: this.versionEdit.aprobado_por,
      es_vigente: this.versionEdit.es_vigente
    };

    if (!this.archivoEdicion) {
      this.actualizarVersionEnBD(cambios, modal);
      return;
    }

    const ruta = this.parametrosRuta(this.versionEdit.numero_version);
    if (!ruta) {
      this.alerta('warning', 'Importante', 'Faltan datos para construir la ruta del archivo (NIT, código, tipo documental o versión)');
      return;
    }

    this.guardandoEdicion = true;
    this.implementacionService.subir(ruta, this.archivoEdicion).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (urlArchivo) => {
        cambios.archivo = urlArchivo;
        cambios.archivo_actualizado = true;
        cambios.archivo_nombre = this.archivoEdicion?.name;
        this.actualizarVersionEnBD(cambios, modal);
      },
      error: (err: any) => {
        this.guardandoEdicion = false;
        this.errorSubida(err);
      }
    });
  }

  private actualizarVersionEnBD(cambios: any, modal: any): void {
    this.guardandoEdicion = true;
    this.implementacionService.actualizarVersion(this.versionEdit.versionId, cambios).pipe(
      finalize(() => { this.guardandoEdicion = false; }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.alerta('success', 'Éxito', 'Versión actualizada correctamente');
        this.cargarVersiones();
        this.cargarHistorial();
        this.archivoEdicion = null;
        this.rutaEdicionPreview = '';
        modal.close();
      },
      error: (err: any) => {
        console.error('Error al actualizar versión:', err);
        this.alerta('error', 'Error', this.mensajeError(err, 'No se pudo actualizar la versión'));
      }
    });
  }

  // ───────────────────────── Archivos: ver, descargar, rutas ─────────────────────────

  visualizarVersion(version: any): void {
    const ruta = this.documento_id ? this.parametrosRuta(version?.numero_version) : null;
    if (!ruta) {
      this.alerta('warning', 'Información incompleta', 'Faltan datos para ubicar el archivo (código, tipo documental, versión o NIT de empresa)');
      return;
    }

    this.visualizandoId = version?.id ?? null;
    this.implementacionService.obtenerArchivoVisualizacion(ruta, version?.archivo).pipe(
      finalize(() => { this.visualizandoId = null; }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: (vista) => {
        if (vista.formato !== 'pdf') {
          this.descargandoId = version?.id ?? null;
          this.implementacionService.descargarUrl(vista.url, ruta).pipe(
            finalize(() => { this.descargandoId = null; }),
            takeUntil(this.destroy$)
          ).subscribe({
            next: () => this.alerta('info', 'Descarga iniciada', 'Los archivos Word y Excel se descargan en el dispositivo.'),
            error: (err: any) => this.errorArchivo(err, 'descargar')
          });
          return;
        }
        const modalRef = this.vistaPrevia.open(vista.url, `Documento ${this.formData.codigo} - v${version.numero_version}`);
        const liberar = () => { if (vista.url.startsWith('blob:')) { window.URL.revokeObjectURL(vista.url); } };
        modalRef.result.then(liberar, liberar);
      },
      error: (err: any) => this.errorArchivo(err, 'visualizar')
    });
  }

  descargarVersion(version: any): void {
    const ruta = this.documento_id ? this.parametrosRuta(version?.numero_version) : null;
    if (!ruta) {
      this.alerta('warning', 'Información incompleta', 'Faltan datos para ubicar el archivo (código, tipo documental, versión o NIT de empresa)');
      return;
    }

    this.descargandoId = version?.id ?? null;
    this.implementacionService.descargar(ruta, version?.archivo).pipe(
      finalize(() => { this.descargandoId = null; }),
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => this.alerta('success', 'Éxito', 'Documento descargado correctamente'),
      error: (err: any) => this.errorArchivo(err, 'descargar')
    });
  }

  onArchivoSeleccionado(event: any): void {
    this.archivoNuevo = event.target.files[0] || null;
    this.actualizarRutaPreview();
  }

  onArchivoEdicionSeleccionado(event: any): void {
    this.archivoEdicion = event.target.files[0] || null;
    this.actualizarRutaPreview();
  }

  /** Recalcula las rutas de vista previa (formulario y modal). */
  actualizarRutaPreview(): void {
    const rutaNueva = this.archivoNuevo ? this.parametrosRuta(this.versionData.numero_version) : null;
    this.rutaArchivoPreview = rutaNueva ? this.implementacionService.construirRutaArchivo(rutaNueva, this.archivoNuevo!) : '';

    const rutaEdicion = this.archivoEdicion ? this.parametrosRuta(this.versionEdit.numero_version) : null;
    this.rutaEdicionPreview = rutaEdicion ? this.implementacionService.construirRutaArchivo(rutaEdicion, this.archivoEdicion!) : '';
  }

  private nombreDocumento(): string {
    return this.nombresPorId.get(Number(this.formData.nombre_documento)) || '';
  }

  /** Datos mínimos para ubicar el archivo; null si falta alguno (no se usan valores por defecto). */
  private parametrosRuta(numeroVersion: string | number): RutaVersion | null {
    const nombreDocumento = this.nombreDocumento();
    const version = String(numeroVersion ?? '').trim();
    if (!this.empresaNit || !this.formData.codigo || !nombreDocumento || !version) {
      return null;
    }
    return {
      nit: this.empresaNit,
      codigo: this.formData.codigo,
      nombreDocumento,
      numeroVersion: version
    };
  }

  // ───────────────────────── Helpers ─────────────────────────

  private errorSubida(err: any): void {
    console.error('Error al subir archivo:', err);
    const mensaje = err?.message === 'FORMATO_NO_SOPORTADO'
      ? 'Solo se permiten archivos PDF, Excel (.xls, .xlsx) o Word (.doc, .docx).'
      : 'No se pudo subir el archivo al almacenamiento en la nube';
    this.alerta('error', 'Error', mensaje);
  }

  private errorArchivo(err: any, accion: string): void {
    console.error(`Error al ${accion} archivo:`, err);
    if (err?.message === 'VENTANA_BLOQUEADA') {
      this.alerta('warning', 'Ventana bloqueada', 'Permita las ventanas emergentes para visualizar el archivo.');
      return;
    }
    const base = err?.status === 404
      ? 'No se encontró el archivo de esta versión en el almacenamiento.'
      : 'No se pudo obtener el archivo de esta versión.';
    const detalle = err?.mensaje ? ` Respuesta del servidor: ${err.mensaje}.` : '';
    const ubicacion = err?.carpeta ? ` Carpeta consultada: ${err.carpeta}/` : '';
    this.alerta('error', `No se pudo ${accion}`, `${base}${detalle}${ubicacion}`);
  }

  private crearVersionVacia(): any {
    return {
      numero_version: '',
      fecha_aprobacion: '',
      aprobado_por: '',
      es_vigente: false
    };
  }

  private fechaEsFutura(fecha?: string): boolean {
    return !!fecha && String(fecha).substring(0, 10) > this.hoy;
  }

  private alerta(icon: string, title: string, text: string): void {
    Swal.fire({ icon, title, text, confirmButtonText: 'Aceptar', confirmButtonColor: '#3085d6' });
  }

  private mensajeError(err: any, porDefecto: string): string {
    const base = err?.error?.error || porDefecto;
    const detalle = err?.error?.detalle;
    return detalle ? `${base} ${detalle}` : base;
  }
}